<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useLintNotesStore, type LintNote } from '../stores/lint_notes'
import { useSynthesisSuggestionsStore } from '../stores/synthesis_suggestions'
import TodayCard from './TodayCard.vue'

// "Review" on Today: the head of both review queues (Lint notes and
// Synthesis drafts), newest first, with a link into each queue. The
// stores are kept live by useAdminSubscriptions, so this only reads.
const PREVIEW_LIMIT = 3

const lint = useLintNotesStore()
const synthesis = useSynthesisSuggestionsStore()
const { notes, openCount: lintOpen, loaded: lintLoaded } = storeToRefs(lint)
const { suggestions, openCount: synthesisOpen, loaded: synthesisLoaded } = storeToRefs(synthesis)

const LINT_KIND_LABEL: Record<LintNote['kind'], string> = {
  contradiction: 'Contradiction',
  stale_claim: 'Stale claim',
  unreinforced_claim: 'Unreinforced claim',
}

interface ReviewRow {
  key: string
  kind: string
  kindClass: string
  title: string
  to: string
}

const rows = computed<ReviewRow[]>(() => {
  const lintRows = notes.value.map<ReviewRow>((note) => ({
    key: `lint-${note.id}`,
    kind: LINT_KIND_LABEL[note.kind] ?? 'Lint note',
    kindClass: note.kind === 'contradiction' ? 'bg-status-alert/15 text-status-alert' : 'bg-factory/15 text-factory',
    title: note.page_path,
    to: '/lint',
  }))
  const synthesisRows = suggestions.value.map<ReviewRow>((s) => ({
    key: `synthesis-${s.id}`,
    kind: 'Synthesis draft',
    kindClass: 'bg-brand-primary/15 text-brand-primary',
    title: s.title,
    to: '/synthesis',
  }))
  return [...lintRows, ...synthesisRows].slice(0, PREVIEW_LIMIT)
})

const total = computed(() => lintOpen.value + synthesisOpen.value)
const loading = computed(() => !lintLoaded.value || !synthesisLoaded.value)
const linkLabel = computed(() => (total.value > 0 ? `All ${total.value}` : 'Open queue'))
</script>

<template>
  <TodayCard title="Review" tone="attention" link-to="/lint" :link-label="linkLabel" testid="today-review">
    <div v-if="loading && rows.length === 0" class="flex flex-col gap-3 py-2" aria-label="Loading review items">
      <span class="h-3.5 w-2/3 rounded bg-border-divider" />
      <span class="h-3.5 w-1/2 rounded bg-border-divider" />
    </div>
    <div v-else-if="rows.length === 0" class="py-3" data-testid="today-review-empty">
      <p class="m-0 font-serif text-xl text-ink">Nothing to review.</p>
      <p class="m-0 mt-1 text-sm text-ink-muted">Lint runs nightly; Synthesis drafts arrive monthly.</p>
    </div>
    <ul v-else class="m-0 list-none p-0">
      <li v-for="row in rows" :key="row.key" class="border-t border-border-divider first:border-t-0">
        <RouterLink
          :to="row.to"
          class="flex flex-col gap-1.5 py-3 text-ink no-underline"
          data-testid="today-review-row"
        >
          <span class="self-start rounded-full px-2 py-0.5 text-xs font-semibold" :class="row.kindClass">{{
            row.kind
          }}</span>
          <span class="truncate font-medium">{{ row.title }}</span>
        </RouterLink>
      </li>
    </ul>
  </TodayCard>
</template>
